import { Link } from "react-router-dom";
import LegalLayout, { Section } from "@/components/LegalLayout";
import { COMPANY } from "@/lib/legal";

const TOC = [
  { id: "pengendali", label: "1. Pengendali Data" },
  { id: "data", label: "2. Data yang Kami Kumpulkan" },
  { id: "tujuan", label: "3. Tujuan Penggunaan" },
  { id: "dasar", label: "4. Dasar Hukum (UU PDP)" },
  { id: "pembayaran", label: "5. Data Pembayaran" },
  { id: "cookies", label: "6. Cookies" },
  { id: "pihak3", label: "7. Penyedia Layanan Pihak Ketiga" },
  { id: "leaddata", label: "8. Data Lead yang Anda Kumpulkan" },
  { id: "retensi", label: "9. Retensi Data" },
  { id: "keamanan", label: "10. Keamanan" },
  { id: "transfer", label: "11. Transfer Data" },
  { id: "hak", label: "12. Hak Anda" },
  { id: "anak", label: "13. Anak-anak" },
  { id: "perubahan", label: "14. Perubahan" },
  { id: "kontak", label: "15. Kontak" },
];

const link = "text-sky-600 font-medium hover:underline";
const ul = "list-disc pl-5 space-y-1.5";

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      seoTitle={`Kebijakan Privasi — ${COMPANY.brand}`}
      seoDesc={`Bagaimana ${COMPANY.brand} mengumpulkan, menggunakan, dan melindungi data Anda sesuai UU Pelindungan Data Pribadi (UU 27/2022).`}
      path="/privacy"
      title="Kebijakan Privasi"
      subtitle={`Kami menghormati privasi Anda. Halaman ini menjelaskan data apa yang kami kumpulkan, mengapa, dan bagaimana kami melindunginya — sesuai UU No. 27/2022 tentang Pelindungan Data Pribadi.`}
      toc={TOC}
    >
      <Section id="pengendali" title="1. Pengendali Data">
        <p>
          {COMPANY.brand} ({COMPANY.website}) bertindak sebagai pengendali data atas informasi pribadi yang
          dikumpulkan melalui Layanan. Anda dapat menghubungi kami terkait privasi melalui{" "}
          <a href={`mailto:${COMPANY.email}`} className={link}>{COMPANY.email}</a> atau WhatsApp{" "}
          <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noreferrer" className={link}>{COMPANY.whatsappDisplay}</a>.
        </p>
      </Section>

      <Section id="data" title="2. Data yang Kami Kumpulkan">
        <ul className={ul}>
          <li><b>Data akun:</b> nama, alamat email, nomor telepon, kata sandi (terenkripsi), foto profil.</li>
          <li><b>Data penggunaan & teknis:</b> alamat IP, perangkat, browser, log akses, dan aktivitas dalam aplikasi.</li>
          <li><b>Data transaksi:</b> riwayat paket/kredit, status pembayaran, nomor invoice (tanpa data kartu).</li>
          <li><b>Data konektor (BYOK):</b> token/kredensial pihak ketiga yang Anda hubungkan (mis. token Fonnte, Gmail App Password) — disimpan dalam bentuk <b>terenkripsi</b>.</li>
          <li><b>Data komunikasi:</b> pesan dukungan, formulir kontak, dan pengaduan.</li>
        </ul>
      </Section>

      <Section id="tujuan" title="3. Tujuan Penggunaan">
        <ul className={ul}>
          <li>Menyediakan, mengoperasikan, dan memelihara Layanan serta fitur-fiturnya.</li>
          <li>Memproses pembayaran, langganan, dan kuota kredit.</li>
          <li>Komunikasi operasional, notifikasi, dan dukungan pelanggan.</li>
          <li>Keamanan, pencegahan penyalahgunaan/penipuan, dan kepatuhan hukum.</li>
          <li>Analitik untuk meningkatkan kualitas dan keandalan Layanan.</li>
        </ul>
      </Section>

      <Section id="dasar" title="4. Dasar Hukum (UU PDP)">
        <p>
          Pemrosesan data dilakukan berdasarkan: (a) pelaksanaan kontrak layanan dengan Anda; (b) persetujuan
          Anda; (c) kepatuhan terhadap kewajiban hukum; dan (d) kepentingan sah kami yang seimbang, sesuai
          UU No. 27/2022 tentang Pelindungan Data Pribadi.
        </p>
      </Section>

      <Section id="pembayaran" title="5. Data Pembayaran">
        <p>
          Pembayaran diproses oleh mitra payment gateway resmi (<b>Midtrans</b> dan/atau <b>iPaymu</b>). Kami
          <b> tidak menyimpan</b> nomor kartu, CVV, atau kredensial pembayaran Anda. Data tersebut dikumpulkan
          dan diproses langsung oleh penyedia pembayaran sesuai kebijakan privasi dan standar keamanan mereka
          (mis. PCI-DSS). Kami hanya menyimpan status transaksi dan metadata invoice.
        </p>
      </Section>

      <Section id="cookies" title="6. Cookies & Teknologi Serupa">
        <p>
          Kami menggunakan cookies dan teknologi serupa untuk autentikasi sesi, fungsionalitas, preferensi, dan
          analitik. Anda dapat mengatur preferensi cookie melalui pengaturan browser. Menonaktifkan cookie
          tertentu dapat memengaruhi fungsi Layanan (mis. tetap masuk/login).
        </p>
      </Section>

      <Section id="pihak3" title="7. Penyedia Layanan Pihak Ketiga">
        <p>Kami dapat membagikan data terbatas kepada penyedia tepercaya yang membantu operasi kami, antara lain:</p>
        <ul className={ul}>
          <li><b>Midtrans / iPaymu</b> — pemrosesan pembayaran.</li>
          <li><b>Clerk</b> — autentikasi & manajemen identitas pengguna.</li>
          <li><b>Brevo</b> — pengiriman email transaksional/notifikasi.</li>
          <li><b>Apify</b> — infrastruktur pengambilan data publik.</li>
          <li><b>Fonnte</b> — gateway WhatsApp (saat Anda menghubungkan akun BYOK).</li>
          <li>Penyedia hosting/cloud untuk penyimpanan & komputasi.</li>
        </ul>
        <p>Mitra tersebut terikat untuk menggunakan data hanya sesuai tujuan yang kami tetapkan.</p>
      </Section>

      <Section id="leaddata" title="8. Data Lead yang Anda Kumpulkan">
        <p>
          Saat Anda mengumpulkan data bisnis/kontak publik melalui Layanan, <b>Anda</b> bertindak sebagai
          pengendali data atas data tersebut, dan {COMPANY.brand} sebagai pemroses. Anda bertanggung jawab
          memastikan pengumpulan dan penggunaan data tersebut mematuhi UU PDP dan peraturan terkait, termasuk
          menghormati permintaan penghapusan atau penolakan (opt-out) dari subjek data.
        </p>
      </Section>

      <Section id="retensi" title="9. Retensi Data">
        <p>
          Kami menyimpan data selama akun aktif atau selama diperlukan untuk tujuan yang dijelaskan dan untuk
          memenuhi kewajiban hukum/akuntansi. Setelah tidak diperlukan, data akan dihapus atau dianonimkan secara
          aman.
        </p>
      </Section>

      <Section id="keamanan" title="10. Keamanan">
        <p>
          Kami menerapkan langkah teknis dan organisasi yang wajar, termasuk enkripsi kata sandi dan kredensial
          konektor (BYOK), kontrol akses, serta transport terenkripsi (HTTPS). Meski demikian, tidak ada sistem
          yang 100% aman — mohon jaga kerahasiaan kredensial akun Anda.
        </p>
      </Section>

      <Section id="transfer" title="11. Transfer Data">
        <p>
          Sebagian penyedia layanan kami dapat memproses data di luar Indonesia. Dalam hal demikian, kami
          mengupayakan perlindungan yang memadai sesuai UU PDP dan praktik industri yang berlaku.
        </p>
      </Section>

      <Section id="hak" title="12. Hak Anda sebagai Subjek Data">
        <ul className={ul}>
          <li>Mengakses dan memperoleh salinan data pribadi Anda.</li>
          <li>Memperbaiki atau memperbarui data yang tidak akurat.</li>
          <li>Menghapus data ("hak untuk dilupakan") sesuai ketentuan hukum.</li>
          <li>Membatasi atau menolak pemrosesan tertentu, serta menarik persetujuan.</li>
          <li>Meminta portabilitas data.</li>
        </ul>
        <p>Untuk menggunakan hak ini, hubungi <a href={`mailto:${COMPANY.email}`} className={link}>{COMPANY.email}</a>. Kami akan menanggapi sesuai jangka waktu yang diatur hukum.</p>
      </Section>

      <Section id="anak" title="13. Anak-anak">
        <p>
          Layanan tidak ditujukan untuk anak di bawah 18 tahun. Kami tidak dengan sengaja mengumpulkan data
          anak. Jika Anda yakin seorang anak telah memberikan data, hubungi kami untuk penghapusan.
        </p>
      </Section>

      <Section id="perubahan" title="14. Perubahan Kebijakan">
        <p>
          Kami dapat memperbarui Kebijakan Privasi ini. Perubahan material akan diumumkan melalui situs atau
          email. Tanggal "Terakhir diperbarui" di atas menunjukkan versi terbaru.
        </p>
      </Section>

      <Section id="kontak" title="15. Kontak">
        <p>
          Pertanyaan atau permintaan terkait privasi dapat dikirim ke{" "}
          <a href={`mailto:${COMPANY.email}`} className={link}>{COMPANY.email}</a>. Lihat juga{" "}
          <Link to="/terms" className={link}>Syarat &amp; Ketentuan</Link> dan{" "}
          <Link to="/refund" className={link}>Kebijakan Pengembalian Dana</Link>.
        </p>
      </Section>
    </LegalLayout>
  );
}
